import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ChatMessageProps } from "./components/ChatMessage";

import {
    AI_HEALTH_STATUS,
    getBalanceChartAssistantAnswer,
    getBalanceChartHealth,
    IBalanceCandidate,
    IBalanceSituation,
} from "services/ai";

import { generateID } from "utils";

type ActiveRequest = {
    controller: AbortController;
    userMessageId: ChatMessageProps["id"];
    assistantMessageId: ChatMessageProps["id"];
};

const INITIAL_QUESTION = "Tell me about this balance chart";

const useChatbot = (
    candidatesPayload: IBalanceCandidate[],
    situationPayload: IBalanceSituation | null
): {
    openChatbox: boolean;
    isLoading: boolean;
    isOnline: boolean;
    sizeMode: "default" | "compact" | "maximized";
    messages: ChatMessageProps[];
    messageInput: string;
    setMessageInput: React.Dispatch<React.SetStateAction<string>>;
    handleOpenChatbox: (e: React.MouseEvent<HTMLDivElement>) => void;
    handleCloseChatbox: (e: React.SyntheticEvent) => void;
    handleToggleMaximize: (e: React.SyntheticEvent) => void;
    handleToggleCompactSize: (e: React.SyntheticEvent) => void;
    handleSendMessage: () => void;
    handleAbortMessage: () => void;
} => {
    const [searchParams] = useSearchParams();
    const insightId = searchParams.get("insightId");

    const [openChatbox, setOpenChatbox] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isOnline, setIsOnline] = useState(false);
    const [isAssistantReady, setIsAssistantReady] = useState(false);
    const hasSentInitialPromptRef = useRef(false);

    const storedConversation = sessionStorage.getItem("balanceChartConversation");

    const initialText =
        "**{highlightedBold}Hi, I'm Eddy - your Leadership Dynamics AI Assistant! I'm here to help you analyse leadership patterns, identify gaps, and benchmark your teams.** \n\n";
    const hasAddedInitialText = useRef(false || Boolean(storedConversation));

    const [sizeMode, setSizeMode] = useState<"default" | "compact" | "maximized">("default");

    const [messages, setMessages] = useState<ChatMessageProps[]>([]);
    const [messageInput, setMessageInput] = useState("");

    const activeRequestRef = useRef<ActiveRequest | null>(null);

    const closeChatbox = () => {
        setOpenChatbox(false);
        setSizeMode((prevSizeMode) => (prevSizeMode === "compact" ? "compact" : "default"));
    };

    const handleOpenChatbox = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        setOpenChatbox(true);
    };

    const handleCloseChatbox = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        closeChatbox();
    };

    const handleToggleMaximize = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        setSizeMode((prevSizeMode) => (prevSizeMode === "maximized" ? "default" : "maximized"));
    };

    const handleToggleCompactSize = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        setSizeMode((prevSizeMode) => (prevSizeMode === "compact" ? "default" : "compact"));
    };

    const handleAbortMessage = () => {
        const activeRequest = activeRequestRef.current;

        if (!activeRequest) {
            return;
        }

        activeRequest.controller.abort();

        // Remove user's question and assistant response.
        setMessages((currentMessages) =>
            currentMessages.filter(
                (message) =>
                    message.id !== activeRequest.assistantMessageId && message.id !== activeRequest.userMessageId
            )
        );

        activeRequestRef.current = null;
        setIsLoading(false);
    };

    const sendPrompt = async (
        question: string,
        showUserMessage = true,
        messageType: ChatMessageProps["type"] = "assistant"
    ) => {
        if (isLoading || insightId === null) return;

        const controller = new AbortController();
        const userMessageId = generateID();
        const assistantMessageId = generateID();

        activeRequestRef.current = { controller, userMessageId, assistantMessageId };

        let hasReceivedResponse = false;
        let streamCompleted = false;

        setIsLoading(true);

        setMessages((currentMessages) => [
            ...currentMessages,
            ...(showUserMessage ? [{ id: userMessageId, type: "user" as const, content: question }] : []),
            {
                id: assistantMessageId,
                type: messageType,
                content: "",
            },
        ]);

        try {
            await getBalanceChartAssistantAnswer({
                queryStrings: {
                    project_insight_id: insightId,
                },
                payload: {
                    balance_data: {
                        candidates: candidatesPayload,
                        situation: situationPayload || null,
                    },
                    question,
                },
                signal: controller.signal,
                onAnswerChunk: async (chunk) => {
                    let messageChunk = "";
                    const hasContent = chunk.trim().length > 0;

                    if (!hasReceivedResponse && hasContent) {
                        hasReceivedResponse = true;
                        setIsLoading(false);
                    }

                    messageChunk = !hasAddedInitialText.current ? initialText + chunk : chunk;
                    hasAddedInitialText.current = true;

                    setMessages((currentMessages) =>
                        currentMessages.map((message) =>
                            message.id === assistantMessageId
                                ? { ...message, content: message.content + messageChunk }
                                : message
                        )
                    );
                },
            });

            streamCompleted = true;
        } catch (error) {
            if ((error as DOMException)?.name === "AbortError") return;
            console.error("Error fetching assistant response:", error);
            setMessages((currentMessages) =>
                currentMessages.map((message) =>
                    message.id === assistantMessageId
                        ? { ...message, content: "Sorry, something went wrong." }
                        : message
                )
            );
        } finally {
            if (activeRequestRef.current?.controller === controller) {
                activeRequestRef.current = null;
            }
            setIsLoading(false);

            // Save the conversation to sessionStorage if tream completed, response has content and the request was not aborted.
            if (streamCompleted && hasReceivedResponse && !controller.signal.aborted) {
                setMessages((currentMessages) => {
                    sessionStorage.setItem("balanceChartConversation", JSON.stringify(currentMessages));
                    return currentMessages;
                });
            }
        }
    };

    const handleSendMessage = () => {
        const trimmedMessage = messageInput.trim();

        if (!trimmedMessage || isLoading) return;
        setMessageInput("");
        sendPrompt(trimmedMessage);
    };

    useEffect(() => {
        if (!openChatbox) {
            return undefined;
        }

        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closeChatbox();
            }
        };

        window.addEventListener("keydown", handleEscapeKey);

        return () => {
            window.removeEventListener("keydown", handleEscapeKey);
        };
    }, [openChatbox]);

    // Check AI assisstant health on component mount
    useEffect(() => {
        let isActive = true;

        // Load conversation from sessionStorage if exists.
        const saved = sessionStorage.getItem("balanceChartConversation");

        if (saved) {
            try {
                setMessages(JSON.parse(saved));
            } catch (error) {
                console.error("Error parsing saved conversation:", error);
            }
        }

        const checkHealth = async () => {
            try {
                // Check Assitant status
                const { data } = await getBalanceChartHealth();

                if (!isActive) return;

                const isReady = data.health.status === AI_HEALTH_STATUS.good;
                setIsOnline(data.health.status !== AI_HEALTH_STATUS.offline);
                setIsAssistantReady(isReady);
            } catch (error) {
                if (!isActive) return;
                console.error("Error checking assistant health:", error);
                setIsOnline(false);
                setIsAssistantReady(false);
            }
        };

        checkHealth();

        return () => {
            isActive = false;
            activeRequestRef.current?.controller.abort();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Send initial prompt if assistant is ready and the chatbox is opened for the first time.
    useEffect(() => {
        if (!openChatbox || !isAssistantReady || hasSentInitialPromptRef.current || messages.length > 0) return;

        sendPrompt(INITIAL_QUESTION, false, "assistant");
        hasSentInitialPromptRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openChatbox, isAssistantReady]);

    return {
        openChatbox,
        isLoading,
        isOnline,
        sizeMode,
        messages,
        messageInput,
        setMessageInput,
        handleOpenChatbox,
        handleCloseChatbox,
        handleToggleMaximize,
        handleToggleCompactSize,
        handleSendMessage,
        handleAbortMessage,
    };
};

export default useChatbot;
